import ApiService from '@core/api/services/ApiService';
import EcommerceService from '@core/ecommerce/services/EcommerceService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { downloadFile } from '@helpers/download-b64-file';
import { ReactComponent as CheckCircle } from '@img/icons/check-circle.svg';
import { ReactComponent as Chevron } from '@img/icons/chevron-right.svg';
import Button from '@stories/Components/Buttons/Button/Button';
import StyledSection from '@stories/Components/Containers/StyledSection/StyledSection';
import Paragraph from '@stories/Components/Global/Typography/Paragraph';
import ExtendHire from '@stories/Components/Hires/ExtendHire/ExtendHire';
import HireFilter from '@stories/Components/Hires/HireFilter/HireFilter';
import HireTable from '@stories/Components/Hires/HireTable/HireTable';
import Modal from '@stories/Components/Media/Modal/Modal';
import Loader from '@stories/Components/Misc/Loader/Loader';
import addDays from 'date-fns/addDays';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StringParam, useQueryParam } from 'use-query-params';
import { ViewHiresStyles as S } from './ViewHires.styles';
const DropdownKeys = {
    allHires: 'AllHires',
    liveHires: 'LiveHires',
};
const PAGE_SIZE = 20;
const ViewHiresListing = (props) => {
    const [modal, setModal] = useState('none');
    const [page, setPage] = useState(1);
    const [dateFrom, setDateFrom] = useState(addDays(new Date(), -365));
    const [dateTo, setDateTo] = useState(addDays(new Date(), 365));
    const [searchTerm, setSearchTerm] = useQueryParam('searchTerm', StringParam);
    const [category, setCategory] = useState(DropdownKeys.allHires);
    const [currentPageResults, setCurrentPageResults] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const totalPages = useMemo(() => Math.ceil(totalResults / PAGE_SIZE), [totalResults]);
    const [isLoading, setIsLoading] = useState(true);
    const [trigger, setTrigger] = useState(new Date().getTime());
    const [selectedExtend, setSelectedExtend] = useState(null);
    const [accountId, setAccountId] = useState(props.primaryAccountId);
    const emailAddress = useSelector((x) => x.app.emailAddress);
    const fetchHires = async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.request({
                baseUrl: UrlResolver.getDomain(),
                controller: 'g42',
                method: 'GET',
                slug: 'hires/search',
                params: {
                    searchTerm: searchTerm ? searchTerm : '',
                    liveOnly: category === DropdownKeys.liveHires,
                    dateFrom: EcommerceService.getApiFormattedDate(dateFrom),
                    dateTo: EcommerceService.getApiFormattedDate(dateTo),
                    page,
                    pageSize: PAGE_SIZE,
                    accountId: accountId?.id,
                },
            });
            setCurrentPageResults(response.result.g42Orders);
            setTotalResults(response.result.resultsTotal);
        }
        catch {
            setCurrentPageResults([]);
        }
        setIsLoading(false);
    };
    useEffect(() => {
        setPage(1);
        setTrigger(new Date().getTime());
    }, [category, searchTerm, dateFrom, dateTo]);
    useEffect(() => {
        fetchHires();
    }, [page, trigger]);
    const handleExport = async () => {
        setModal('loading');
        const response = await ApiService.request({
            baseUrl: UrlResolver.getDomain(),
            controller: 'g42',
            method: 'GET',
            slug: 'hires/download-hires-report',
            params: {
                searchTerm: searchTerm,
                liveOnly: category === DropdownKeys.liveHires,
                emailAddress,
            },
        });
        if (response.result.fileEmailed) {
            setModal('export-emailed');
        }
        else {
            downloadFile(response.result.fileAsBase64, `hires-report-${EcommerceService.getApiFormattedDate(new Date())}`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            setModal('export-download');
        }
    };
    const handleExtend = (hire) => {
        if (!hire.onHire) {
            return;
        }
        setSelectedExtend(hire);
        setModal('extend');
    };
    const handleExtendConfirm = () => {
        setModal('none');
        setTrigger(new Date().getTime());
    };
    const renderModalContent = () => {
        if (modal === 'none') {
            return null;
        }
        if (modal === 'extend') {
            return (React.createElement(S.ModalInner, null, selectedExtend !== null && (React.createElement(ExtendHire, { hire: selectedExtend, translations: props.translations, onConfirm: handleExtendConfirm, accountId: accountId?.id }))));
        }
        if (modal === 'loading') {
            return (React.createElement(S.ModalInner, null,
                React.createElement(React.Fragment, null, React.createElement(Loader, null))));
        }
        if (modal === 'export-download') {
            return (React.createElement(S.ModalInner, null,
                React.createElement(S.Svg, null,
                    React.createElement(CheckCircle, null)),
                React.createElement(Paragraph, { size: "regular", align: 'center' }, props.translations['g42.hires.exportready']),
                React.createElement(Button, { branding: "black", title: props.translations['g42.hires.returntoaccount'], onClick: () => setModal('none') })));
        }
        if (modal === 'export-emailed') {
            return (React.createElement(S.ModalInner, null,
                React.createElement(S.Svg, null,
                    React.createElement(CheckCircle, null)),
                React.createElement(Paragraph, { size: "regular", align: 'center' }, props.translations['g42.hires.exportsenttoemail']),
                React.createElement(Button, { branding: "black", title: props.translations['g42.hires.returntoaccount'], onClick: () => setModal('none') })));
        }
        return null;
    };
    const Inner = (React.createElement(React.Fragment, null,
        React.createElement(HireFilter, { searchTerm: searchTerm ? searchTerm : '', onSearchTermChanged: setSearchTerm, onSearchClick: fetchHires, dropdownOptions: [
                {
                    id: DropdownKeys.allHires,
                    value: 'All Hires',
                },
                {
                    id: DropdownKeys.liveHires,
                    value: 'Live Hires',
                },
            ], dateTo: dateTo, onDateToChanged: setDateTo, dateFrom: dateFrom, onDateFromChanged: setDateFrom, category: category, onCategoryChanged: setCategory, selectedAccountId: accountId, primaryAccountId: props.primaryAccountId, secondaryAccountIds: props.secondaryAccountIds, onAccountChanged: setAccountId, labels: {
                despatchFromLabel: props.translations['g42.hires.formlabels.dispatchfrom'],
                despatchToLabel: props.translations['g42.hires.formlabels.dispatchto'],
                searchLabel: props.translations['g42.hires.formlabels.search'],
                showLabel: props.translations['g42.hires.formlabels.show'],
                accountLabel: props.translations['g42.hires.formlabels.account'],
            }, placeholders: {
                searchPlaceholder: props.translations['g42.hires.placeholders.search'],
                selectDatePlaceholder: props.translations['g42.hires.placeholders.selectdate'],
            } }),
        React.createElement(HireTable, { isLoading: isLoading, onExtend: handleExtend, labels: {
                extendLabel: props.translations['g42.hires.formlabels.extend'],
                offHireLabel: props.translations['g42.hires.formlabels.offhire'],
                offHireDisclaimerTitle: props.translations['g42.hires.formlabels.offhire.disclaimertitle'],
                offHireDisclaimerCopy: props.translations['g42.hires.formlabels.offhire.disclaimercopy'],
                onHireLabel: props.translations['g42.hires.formlabels.onhire'],
                orderDocumentLabel: props.translations['g42.hires.formlabels.orderdocument'],
                orderNoLabel: props.translations['g42.hires.formlabels.ordernumber'],
                trackingUrlLabel: props.translations['g42.hires.formlabels.trackingurl'],
                siteLabel: props.translations['g42.hires.formlabels.site'],
                yourRefLabel: props.translations['g42.hires.formlabels.ref'],
                downloadLabel: props.translations['g42.hires.formlabels.download'],
                noResultsFoundLabel: props.translations['g42.hires.noresults'],
            }, items: currentPageResults }),
        React.createElement(S.ControlsWrapper, null,
            currentPageResults && currentPageResults.length > 0 && (React.createElement(Button, { branding: "black", title: props.translations['g42.hires.export'], onClick: handleExport })),
            totalPages > 1 ? (React.createElement(S.Pagination, null,
                React.createElement("small", null,
                    "Page ",
                    page,
                    " of ",
                    totalPages),
                React.createElement(S.ArrowButton, { direction: "left", onClick: () => setPage(page - 1), disabled: page === 1 },
                    React.createElement(Chevron, null)),
                React.createElement(S.ArrowButton, { direction: "right", onClick: () => setPage(page + 1), disabled: page === totalPages },
                    React.createElement(Chevron, null)))) : null)));
    return (React.createElement(S.Wrapper, null,
        React.createElement(S.Inner, null,
            React.createElement(S.Title, null, props.title),
            props.hideContainer === true ? (React.createElement(React.Fragment, null, Inner)) : (React.createElement(StyledSection, { includeCorners: true }, Inner))),
        React.createElement(Modal, { isActive: modal === 'extend' ||
                modal === 'export-emailed' ||
                modal === 'export-download' ||
                modal === 'loading', onClose: () => setModal('none'), size: "small" }, renderModalContent())));
};
export default ViewHiresListing;
